import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { NavBar } from '@/components/ui';
import EventPreviewModal, { type ProfileEvent } from '@/components/common/EventPreviewModal';
import { VolunteerJoinEventModal } from '@/pages/Organizations/components/VolunteerJoinEventModal/VolunteerJoinEventModal';
import type { Organization } from '@/types/events';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/constants/UserContext';
import { isUuid } from '@/utils/isUuid';
import { mapInviteEventRow } from './mapInviteEventRow';

type LoadState = 'loading' | 'ready' | 'invalid' | 'not_found' | 'error';

export function EventInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('event');
  const { user } = useUser();
  const isOrganization = user?.role === 'organization';

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [event, setEvent] = useState<ProfileEvent | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [joinEventModal, setJoinEventModal] = useState<Organization | null>(null);

  const loadEvent = useCallback(async () => {
    if (!isUuid(eventIdParam)) {
      setLoadState('invalid');
      setEvent(null);
      setPreviewOpen(false);
      return;
    }

    setLoadState('loading');
    try {
      const { data, error } = await supabase.rpc('get_event_for_invite', {
        p_event_id: eventIdParam,
      });

      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : data;
      if (!row || typeof row !== 'object') {
        setLoadState('not_found');
        setEvent(null);
        setPreviewOpen(false);
        return;
      }

      const mapped = mapInviteEventRow(row as Record<string, unknown>);
      setEvent(mapped);
      setLoadState('ready');
      setPreviewOpen(true);
    } catch {
      setLoadState('error');
      setEvent(null);
      setPreviewOpen(false);
    }
  }, [eventIdParam]);

  useEffect(() => {
    void loadEvent();
  }, [loadEvent]);

  const closePreview = () => {
    setPreviewOpen(false);
    navigate('/organizations', { replace: true });
  };

  return (
    <Box minH="100vh" bg="white" pl={{ base: 0, lg: 'var(--sidebar-width)' }}>
      <NavBar />
      <Box maxW="800px" mx="auto" py="8" px="5" pb={{ base: 'var(--mobile-bottom-safe)', lg: '8' }}>
        {loadState === 'loading' && (
          <Flex justify="center" py="16">
            <Text fontSize="sm" color="volyx.textSecondary">
              Loading event…
            </Text>
          </Flex>
        )}

        {loadState === 'invalid' && (
          <Box textAlign="center" py="16">
            <Text fontSize="md" fontWeight="semibold" color="volyx.text" mb="2">
              Invalid invite link
            </Text>
            <Text fontSize="sm" color="volyx.textSecondary" mb="4">
              This invite link is missing a valid event id.
            </Text>
            <Button onClick={() => navigate('/organizations')}>Go to dashboard</Button>
          </Box>
        )}

        {loadState === 'not_found' && (
          <Box textAlign="center" py="16">
            <Text fontSize="md" fontWeight="semibold" color="volyx.text" mb="2">
              Event not found
            </Text>
            <Text fontSize="sm" color="volyx.textSecondary" mb="4">
              This event may have ended or the invite link is no longer valid.
            </Text>
            <Button onClick={() => navigate('/organizations')}>Go to dashboard</Button>
          </Box>
        )}

        {loadState === 'error' && (
          <Box textAlign="center" py="16">
            <Text fontSize="md" fontWeight="semibold" color="volyx.text" mb="2">
              Could not load event
            </Text>
            <Text fontSize="sm" color="volyx.textSecondary" mb="4">
              Please try again in a moment.
            </Text>
            <Flex gap="3" justify="center">
              <Button variant="outline" onClick={() => navigate('/organizations')}>
                Go to dashboard
              </Button>
              <Button onClick={() => void loadEvent()}>Retry</Button>
            </Flex>
          </Box>
        )}
      </Box>

      <EventPreviewModal
        key={event?.id ?? 'invite-event'}
        visible={previewOpen && loadState === 'ready'}
        onClose={closePreview}
        event={event}
        isOrganization={isOrganization}
        fromInviteLink
        onJoinEvent={
          isOrganization
            ? undefined
            : (ev) => {
                setPreviewOpen(false);
                setJoinEventModal(ev as Organization);
              }
        }
        onNavigateToVolunteers={(id) => navigate(`/volunteers?event=${encodeURIComponent(id)}`)}
        onOpenVolunteerTasks={(id) => navigate(`/volunteer-tasks?event=${encodeURIComponent(id)}`)}
      />

      <VolunteerJoinEventModal
        isOpen={joinEventModal != null}
        event={joinEventModal}
        onClose={() => {
          setJoinEventModal(null);
          if (event) setPreviewOpen(true);
        }}
        onSuccess={() => {
          setJoinEventModal(null);
          closePreview();
        }}
      />
    </Box>
  );
}
